import React from "react";

import {produce} from "immer";

import * as constants from "@client/constants";
import * as typography from "@client/shared/typography";

import {Icon} from "@client/components/Icon";
import {ManagerShell} from "@client/components/ManagerShell";
import {NoOutstandingRequestsIndicator} from "@client/components/NoOutstandingRequestsIndicator";
import {ActionsDefinition} from "@client/gotterdammerung";
import {Notifications} from "@client/shared/components/Notifications";
import {Static} from "@client/shared/components/Static";
import {useMessages, useSiteContext} from "@client/site/context";
import {classNames, reverse, server} from "@reactivated";

import {Props} from "./Layout";

const Feature = ({
    children,
    announcement,
}: {
    announcement:
        | "HIDDEN_MACROS"
        | "MEAL_PLANS_BETA"
        | "CLIENT_MEASUREMENTS"
        | "MANAGER_FITNESS_BETA"
        | "COPY_MEAL_PLANS";
    children: React.ReactNode;
}) => (
    <div className="feature-card">
        <div>{children}</div>
        <button
            onClick={async () => {
                await server.core.rpc.dismiss_announcement({announcement});
                window.location.reload();
            }}
        >
            <Icon icon="0822-cross2" />
        </button>
    </div>
);

interface ManagerTabsProps {
    title?: string;
    disableHistory?: true;
    extra?: React.ReactNode;
    contentClassName?: string;
    tabs: Array<{
        name: string;
        title: string;
        content: React.ReactNode;
        hasErrors?: boolean;
        icon: constants.Icon | false;
        actions?: ActionsDefinition;
        options?: React.ReactNode;
    }>;
}

export function ManagerTabs({
    title,
    extra,
    disableHistory,
    tabs,
    contentClassName,
}: ManagerTabsProps) {
    const context = useSiteContext();
    const {request, setValue} = context;
    const defaultTab =
        (disableHistory == true
            ? null
            : new URL(request.url).searchParams.get("tab")) ?? tabs[0].name;

    const [currentTab, setCurrentTab] = React.useState(defaultTab);

    const current = tabs.find((t) => t.name == currentTab);

    if (current == null) {
        return <div>Invalid tab</div>;
    }

    return (
        <div className="flex flex-1 flex-col">
            {title != null && (
                <h1
                    className="
                        mb-1 truncate text-lg
                        sm:text-xl
                    "
                >
                    {title}
                </h1>
            )}
            <div className="hidden">
                <label htmlFor="tabs" className="sr-only">
                    Select a tab
                </label>
                {/* Use an "onChange" listener to redirect the user to the selected tab URL. */}
                <select
                    id="tabs"
                    name="tabs"
                    className="
                        form-select block w-full rounded-md border-gray-300 py-2 pl-3
                        text-base
                        focus:border-indigo-500 focus:ring-indigo-500
                        focus:outline-hidden
                        sm:text-sm
                    "
                    value={currentTab}
                    onChange={(event) => setCurrentTab(event.target.value)}
                >
                    {tabs.map((tab, index) => (
                        <option key={index} value={tab.name}>
                            {tab.title}
                        </option>
                    ))}
                </select>
            </div>
            <div className="mb-1 block">
                <div className="border-b border-gray-200">
                    <nav
                        className="
                            -mb-px flex min-h-12 items-center space-x-2
                            sm:space-x-8
                        "
                        aria-label="Tabs"
                    >
                        {tabs
                            .filter((tab) => tab.content != false)
                            .map((tab, index) => (
                                <button
                                    key={tab.name}
                                    onClick={() => {
                                        setCurrentTab(tab.name);

                                        if (disableHistory == true) {
                                            return;
                                        }

                                        if (index == 0) {
                                            history.pushState(
                                                {},
                                                "",
                                                window.location.pathname,
                                            );
                                            setValue({
                                                ...context,
                                                request: produce(request, (draft) => {
                                                    draft.url = `${window.location.origin}${window.location.pathname}`;
                                                }),
                                            });
                                        } else {
                                            history.pushState(
                                                {},
                                                "",
                                                `${window.location.pathname}?tab=${tab.name}`,
                                            );
                                            setValue({
                                                ...context,
                                                request: produce(request, (draft) => {
                                                    draft.url = `${window.location.origin}${window.location.pathname}?tab=${tab.name}`;
                                                }),
                                            });
                                        }
                                    }}
                                    className={classNames(
                                        tab.name == currentTab
                                            ? "border-indigo-500 text-indigo-600"
                                            : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700",
                                        "whitespace-nowrap border-b-2 py-1 sm:py-4 px-1 font-medium text-sm",
                                        tab.hasErrors == true && "text-red-600",
                                    )}
                                    aria-current={
                                        tab.name == currentTab ? "page" : undefined
                                    }
                                >
                                    <div
                                        className="
                                            hidden
                                            sm:block
                                        "
                                    >
                                        {tab.icon == false ? (
                                            tab.title
                                        ) : (
                                            <Icon icon={tab.icon}>{tab.title}</Icon>
                                        )}
                                    </div>
                                    <div
                                        className="
                                            block
                                            sm:hidden
                                        "
                                    >
                                        {tab.title}
                                    </div>
                                </button>
                            ))}
                        <div className="flex flex-1 justify-end" id="more">
                            {extra ?? current.options}
                        </div>
                    </nav>
                </div>
            </div>
            <div className={classNames("flex flex-col flex-1", contentClassName)}>
                {current.content}
            </div>
        </div>
    );
}

export type Breadcrumbs = Array<
    | {link: string; title: string; onClick?: never}
    | {onClick: () => void; title: string; link?: never}
    | {label: string; onClick?: never}
>;

export const ManagerLayout = (
    props: Props & {currentClientManagerId: number | null; breadcrumbs?: Breadcrumbs},
) => {
    const context = useSiteContext();
    const messages = useMessages();

    const {title, metaTitle} =
        typeof props.title === "object"
            ? {title: props.title.page, metaTitle: props.title.meta}
            : {title: props.title, metaTitle: props.title};
    const metaImage =
        props.metaImage ?? `${context.environment.STATIC_URL}images/og-default.png`;
    const announcements = (
        <>
            {context.profile?.user.casted_dismissed_announcements.includes(
                "MANAGER_FITNESS_BETA",
            ) === false && (
                <Feature announcement="MANAGER_FITNESS_BETA">
                    <typography.Subtitle>
                        New Feature: Workout Tracking
                    </typography.Subtitle>
                    <typography.P>
                        Your clients can now track their workouts.
                    </typography.P>
                    <typography.P>
                        And you can view and edit their workouts and progress. Just
                        click on an individual client’s day to get started.
                    </typography.P>
                    <typography.P>
                        You can{" "}
                        <typography.A
                            track="meal_plans_walkthrough"
                            href={`https://calendly.com/joyhealthtracker/support/?name=${context.profile.user.manager_full_name}&email=${context.profile.user.email}&a2=Workouts`}
                            external
                        >
                            schedule a walkthrough of workouts
                        </typography.A>{" "}
                        or start using them on your own.
                    </typography.P>
                    <typography.P>
                        Got questions or feedback? Email us at{" "}
                        <strong>support@joyapp.com</strong>.
                    </typography.P>
                </Feature>
            )}
            {context.profile?.user.casted_dismissed_announcements.includes(
                "COPY_MEAL_PLANS",
            ) === false && (
                <Feature announcement="COPY_MEAL_PLANS">
                    <typography.Subtitle>
                        New Feature: Copy a Meal Plan
                    </typography.Subtitle>
                    <typography.P>
                        Save even more time. View any meal plan and hit Copy to
                        duplicate its contents.
                    </typography.P>
                </Feature>
            )}

            {context.profile?.user.casted_dismissed_announcements.includes(
                "HIDDEN_MACROS",
            ) === false && (
                <Feature announcement="HIDDEN_MACROS">
                    <typography.Subtitle>Announcing Hidden Macros</typography.Subtitle>
                    <typography.P>
                        You can now hide calories and macronutrients from clients.
                    </typography.P>
                    <typography.P>
                        You can disable them for <strong>all</strong> your clients by
                        visiting your settings.
                    </typography.P>
                    <typography.P>
                        Or disable them for a <strong>specific</strong> client by going
                        to the planner for that client.
                    </typography.P>
                </Feature>
            )}

            {context.profile?.user.casted_dismissed_announcements.includes(
                "MEAL_PLANS_BETA",
            ) === false && (
                <Feature announcement="MEAL_PLANS_BETA">
                    <typography.Subtitle>Introducing Meal Plans</typography.Subtitle>
                    <typography.P>
                        Curate and automate different meal plans for your clients. Keep
                        them high level by recommending{" "}
                        <strong>4 oz of lean protein</strong> with{" "}
                        <strong>8 oz of green veggies</strong>.
                    </typography.P>
                    <typography.P>
                        Or be specific with <strong>8 oz of steamed broccoli</strong>{" "}
                        and <strong>12 oz of grilled chicken breast</strong>. Anything
                        goes.
                    </typography.P>
                    <typography.P>
                        You’ll save time. And your clients will get the guidance they
                        need. Automagically.
                    </typography.P>
                    <typography.P>
                        You can{" "}
                        <typography.A
                            track="meal_plans_walkthrough"
                            href={reverse("business_demo")}
                            external
                        >
                            schedule a walkthrough of meal plans
                        </typography.A>{" "}
                        or start using them on your own.
                    </typography.P>
                </Feature>
            )}
            {context.profile?.user.casted_dismissed_announcements.includes(
                "CLIENT_MEASUREMENTS",
            ) === false && (
                <Feature announcement="CLIENT_MEASUREMENTS">
                    <typography.Subtitle>Client Measurements</typography.Subtitle>
                    <typography.P>
                        You can now enter measurements on behalf of your clients.
                    </typography.P>
                    <typography.P>
                        Just click on a client, find the appropriate day, and scroll to
                        the bottom.
                    </typography.P>
                    <typography.P>
                        You can then enter their measurements for them.
                    </typography.P>
                    <typography.P>
                        Got questions or feedback? Email us at{" "}
                        <strong>support@joyapp.com</strong>.
                    </typography.P>
                </Feature>
            )}
        </>
    );
    return (
        <ManagerShell
            currentClientManagerId={props.currentClientManagerId}
            breadCrumbs={props.breadcrumbs}
        >
            <title>{title}</title>
            <meta property="og:title" content={metaTitle} />
            <meta property="og:image" content={metaImage} />
            <meta charSet="utf-8" />

            <meta
                name="viewport"
                content="width=device-width, initial-scale=1, shrink-to-fit=no"
            />

            <link
                rel="icon"
                href={`${context.environment.STATIC_URL}favicon.png`}
                type="image/png"
            />
            <meta property="og:title" content={metaTitle} />
            <meta property="og:image" content={metaImage} />
            <Static />
            <div>{announcements}</div>
            <NoOutstandingRequestsIndicator />
            <div
                className={classNames(
                    "mx-auto",
                    "w-full",
                    "flex-1",
                    "flex",
                    "flex-col",
                    props.narrow === true
                        ? "max-w-[800px]"
                        : props.narrow == "liquid"
                          ? "max-w-[1600px]"
                          : "max-w-[1200px]",
                )}
            >
                <div
                    className="
                        sticky top-16 z-50 -mt-6 mb-4
                        sm:static sm:top-0
                    "
                >
                    <Notifications
                        position="site"
                        notifications={messages.messages}
                        dismissHandler={(notification) => {
                            messages.remove(notification.id);
                        }}
                    />
                </div>
                {props.children}
            </div>
        </ManagerShell>
    );
};
